import { Component, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Moment } from 'moment';

export interface ChangeNoteMonthFilterPayload {
  filterType: 'month';
  fromMonth: string;
  toMonth: string;
}

export interface ChangeNoteMonthFilterDialogData {
  reportName: string;
}

@Component({
  selector: 'change-note-month-filter-dialog',
  templateUrl: './change-note-month-filter-dialog.html',
  styleUrls: ['./change-note-month-filter-dialog.css']
})
export class ChangeNoteMonthFilterDialog {
  fromMonth: Date = null;
  toMonth: Date = null;
  maxAllowedToMonth: Date = null;
  maxMonthRange: number = 12;
  errorMessage: string = '';

  constructor(
    public dialogRef: MatDialogRef<ChangeNoteMonthFilterDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeNoteMonthFilterDialogData
  ) {
    this.maxMonthRange = this.getMaxMonthRangeByScenario(data && data.reportName ? data.reportName : '');
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    this.fromMonth = monthStart;
    this.toMonth = monthStart;
    this.maxAllowedToMonth = this.calculateMaxAllowedMonth(monthStart);
  }

  private getMaxMonthRangeByScenario(reportName: string): number {
    const normalized = (reportName || '').toLowerCase();
    if (normalized.indexOf('tpt') !== -1) {
      return 18;
    }
    return 12;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  //Calculate the maximum allowed To Month given a From Month.
  private calculateMaxAllowedMonth(fromDate: Date): Date {
    if (!fromDate) {
      return null;
    }

    const year = fromDate.getFullYear();
    let month = fromDate.getMonth();

    // Inclusive month count: max To = From + (maxMonthRange - 1)
    month += (this.maxMonthRange - 1);
    let newYear = year;
    if (month > 11) {
      newYear += Math.floor(month / 12);
      month = month % 12;
    }
    return new Date(newYear, month, 1);
  }

  //Calculate the minimum allowed From Month given a To Month
  private calculateMinAllowedMonth(toDate: Date): Date {
    if (!toDate) {
      return null;
    }
    const year = toDate.getFullYear();
    let month = toDate.getMonth();

    // Inclusive month count: min From = To - (maxMonthRange - 1)
    month -= (this.maxMonthRange - 1);
    let newYear = year;
    while (month < 0) {
      month += 12;
      newYear--;
    }

    return new Date(newYear, month, 1);
  }

  //Calculate the number of months between two dates, ex- Jan 2025 to Jun 2025 = 6 months
  private getMonthDifference(fromDate: Date, toDate: Date): number {
    if (!fromDate || !toDate) {
      return 0;
    }

    const fromYear = fromDate.getFullYear();
    const fromMonthNum = fromDate.getMonth() + 1;
    const toYear = toDate.getFullYear();
    const toMonthNum = toDate.getMonth() + 1;

    const monthsDiff = (toYear - fromYear) * 12 + (toMonthNum - fromMonthNum);
    return monthsDiff;
  }

  // Check if the selected range exceeds the maximum allowed range.
  private isRangeExceeded(fromDate: Date, toDate: Date): boolean {
    if (!fromDate || !toDate) {
      return false;
    }

    const monthDifference = this.getMonthDifference(fromDate, toDate);
    return monthDifference >= this.maxMonthRange;
  }

  monthSelected(normalizedMonth: Moment, key: 'from' | 'to', datepicker: { close: () => void }) {
    const selectedMonth = new Date(normalizedMonth.year(), normalizedMonth.month(), 1);

    if (key === 'from') {
      this.fromMonth = selectedMonth;
      this.maxAllowedToMonth = this.calculateMaxAllowedMonth(selectedMonth);
      // Auto-populate To-Month-Year to same month initially
      this.toMonth = selectedMonth;
    } else {
      this.toMonth = selectedMonth;
      // Ensure To is not before From
      if (this.fromMonth && this.getMonthToken(this.toMonth) < this.getMonthToken(this.fromMonth)) {
        this.toMonth = this.fromMonth;
      }
      // Ensure To is within allowed range; if not, reset to max allowed
      if (this.fromMonth && this.isRangeExceeded(this.fromMonth, this.toMonth)) {
        this.maxAllowedToMonth = this.calculateMaxAllowedMonth(this.fromMonth);
        this.toMonth = this.maxAllowedToMonth;
      }
    }

    this.errorMessage = '';
    datepicker.close();
  }

  private getMonthToken(_dt: Date): number {
    return (_dt.getFullYear() * 100) + (_dt.getMonth() + 1);
  }

  private getMonthValue(_dt: Date): string {
    const month = _dt.getMonth() + 1;
    return _dt.getFullYear() + '-' + (month < 10 ? '0' + month : month);
  }

  isRangeValid(): boolean {
    if (!this.fromMonth || !this.toMonth) {
      return false;
    }

    // Check if toMonth is before fromMonth
    if (this.getMonthToken(this.fromMonth) > this.getMonthToken(this.toMonth)) {
      return false;
    }

    // Check if range exceeds 6 months
    if (this.isRangeExceeded(this.fromMonth, this.toMonth)) {
      return false;
    }

    return true;
  }

  download(): void {
    this.errorMessage = '';

    if (!this.isRangeValid()) {
      if (this.isRangeExceeded(this.fromMonth, this.toMonth)) {
        this.errorMessage = '* Maximum allowed range is ' + this.maxMonthRange + ' months from the selected From Month-Year.';
      } else {
        this.errorMessage = '* From Month-Year cannot be greater than To Month-Year';
      }
      return;
    }

    const payload: ChangeNoteMonthFilterPayload = {
      filterType: 'month',
      fromMonth: this.getMonthValue(this.fromMonth),
      toMonth: this.getMonthValue(this.toMonth)
    };

    this.dialogRef.close(payload);
  }
}
